import { useState } from 'react'
import { Bell, X, AlertTriangle, Shield, Wifi, WifiOff, Eye } from 'lucide-react'
import { useRealTimeAlerts } from '@/hooks/useRealTimeAlerts'
import { motion, AnimatePresence } from 'framer-motion'
import { formatDistanceToNow } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

export function AlertBell() {
  const [showDropdown, setShowDropdown] = useState(false)
  const {
    alerts,
    unreadCount,
    isConnected,
    criticalAlerts,
    markAsRead,
    clearAlerts,
    dismissAlert
  } = useRealTimeAlerts()
  
  const handleOpen = () => {
    setShowDropdown(true)
    if (unreadCount > 0) {
      markAsRead()
    }
  }
  
  const getRiskIcon = (level: string) => {
    switch (level) {
      case 'critical':
        return <AlertTriangle className="h-4 w-4 text-risk-critical" />
      case 'high':
        return <Shield className="h-4 w-4 text-risk-high" />
      case 'medium':
        return <Shield className="h-4 w-4 text-risk-medium" />
      default:
        return <Shield className="h-4 w-4 text-risk-low" />
    }
  }
  
  const getRiskBadgeColor = (level: string) => {
    switch (level) {
      case 'critical':
        return 'bg-risk-critical text-white'
      case 'high':
        return 'bg-risk-high text-white'
      case 'medium':
        return 'bg-risk-medium text-white'
      default:
        return 'bg-risk-low text-white'
    }
  }
  
  return (
    <div className="relative">
      {/* Bell Icon */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleOpen}
        className="relative p-2 hover:bg-muted"
      >
        <Bell className="h-5 w-5" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className={`absolute -top-1 -right-1 h-5 w-5 text-white text-xs rounded-full flex items-center justify-center font-semibold ${
              criticalAlerts > 0 ? 'bg-risk-critical animate-pulse' : 'bg-risk-high'
            }`}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
        
        {/* Connection Indicator */}
        <span
          className={`absolute bottom-0 right-0 h-2 w-2 rounded-full ${
            isConnected ? 'bg-green-500' : 'bg-gray-400'
          }`}
          title={isConnected ? 'Connected' : 'Offline'}
        />
      </Button>
      
      {/* Dropdown */}
      <AnimatePresence>
        {showDropdown && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowDropdown(false)}
            />
            
            {/* Alert Panel */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ type: "spring", duration: 0.2 }}
              className="absolute right-0 mt-2 w-96 bg-card border rounded-lg shadow-xl z-50 max-h-[500px] overflow-hidden"
            >
              {/* Header */}
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-foreground" />
                    <h3 className="text-lg font-semibold text-foreground">
                      Real-Time Alerts
                    </h3>
                  </div>
                  
                  {/* Connection Status */}
                  <div className="flex items-center gap-1">
                    {isConnected ? (
                      <>
                        <Wifi className="h-3 w-3 text-green-500" />
                        <span className="text-xs text-green-600 dark:text-green-400">Live</span>
                      </>
                    ) : (
                      <>
                        <WifiOff className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-500">Offline</span>
                      </>
                    )}
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAlerts}
                  className="text-xs h-auto p-1 text-muted-foreground hover:text-foreground"
                >
                  Clear all
                </Button>
              </div>
              
              {/* Stats Bar */}
              {alerts.length > 0 && (
                <div className="px-4 py-2 bg-muted/30 border-b">
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">Total:</span>
                      <Badge variant="secondary" className="h-5 text-xs">
                        {alerts.length}
                      </Badge>
                    </div>
                    {criticalAlerts > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">Critical:</span>
                        <Badge className="bg-risk-critical text-white h-5 text-xs">
                          {criticalAlerts}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Alerts List */}
              <div className="overflow-y-auto max-h-[350px]">
                {alerts.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No active alerts</p>
                    <p className="text-sm mt-1">
                      {isConnected 
                        ? "Fraud alerts will appear here in real-time"
                        : "Connect to receive real-time alerts"
                      }
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {alerts.map((alert) => (
                      <motion.div
                        key={alert.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="p-4 hover:bg-muted/50 transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="flex-shrink-0 mt-0.5">
                              {getRiskIcon(alert.risk_level)}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-sm font-medium text-foreground">
                                  {alert.risk_level === 'critical' && '🚨 '}
                                  Risk Score: {alert.risk_score}%
                                </p>
                                <Badge 
                                  className={`${getRiskBadgeColor(alert.risk_level)} text-xs h-5`}
                                  variant="secondary"
                                >
                                  {alert.risk_level.toUpperCase()}
                                </Badge>
                              </div>
                              
                              <p className="text-xs text-muted-foreground mb-2">
                                Patterns: {alert.patterns_detected.join(', ')}
                              </p>
                              
                              {alert.lead && (
                                <div className="text-xs text-muted-foreground mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono">{alert.lead.email}</span>
                                    <span>•</span>
                                    <span>{alert.lead.source}</span>
                                  </div>
                                </div>
                              )}
                              
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(alert.created_at), {
                                    addSuffix: true
                                  })}
                                </p>
                                
                                {alert.amount && (
                                  <span className="text-xs font-medium text-green-600 dark:text-green-400">
                                    ${alert.amount.toLocaleString()} saved
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                // Navigate to lead details
                                setShowDropdown(false)
                                window.location.href = `/leads?search=${alert.lead_id}`
                              }}
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="View lead details"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => dismissAlert(alert.id)}
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                              title="Dismiss alert"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Footer */}
              {alerts.length > 0 && (
                <div className="px-4 py-3 border-t bg-muted/30">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowDropdown(false)
                      window.location.href = '/alerts'
                    }}
                    className="w-full text-xs h-8"
                  >
                    View All Alert History
                  </Button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}